import * as z from "zod";
import { errorMessage, phoneSchema } from "../../utils";

const WithdrawalStatus = ["PENDING", "COMPLETED", "FAILED"] as const;

const Msisdn = phoneSchema("Phone number is required", "Phone number is invalid").transform((val) => val.slice(2));

const BankCardBankCode = z.enum([
  "AUB",
  "MIDB",
  "BDC",
  "HSBC",
  "CAE",
  "EGB",
  "UB",
  "QNB",
  "ARAB",
  "ENBD",
  "ABK",
  "NBK",
  "ABC",
  "FAB",
  "ADIB",
  "CIB",
  "HDB",
  "MISR",
  "AAIB",
  "EALB",
  "EDBE",
  "FAIB",
  "BLOM",
  "ADCB",
  "BOA",
  "SAIB",
  "NBE",
  "ABRK",
  "POST",
  "NSB",
  "IDB",
  "SCB",
  "MASH",
  "AIB",
  "GASC",
  "ARIB",
  "PDAC",
  "NBG",
  "CBE",
  "BBE",
]);

const InstantBankBankCode = z.enum([
  "AAIB",
  "ABE",
  "ABK",
  "ADCB",
  "AIB",
  "AIBANK",
  "AUB",
  "BDC",
  "CAE",
  "CBE",
  "CIB",
  "CITIBANK",
  "EALB",
  "EBE",
  "ENBD",
  "FAB",
  "FAIB",
  "HDB",
  "HSBC",
  "IDBD",
  "MCDR",
  "MIDBANK",
  "NBE",
  "NBG",
  "NBK",
  "NSB",
  "QNB",
  "SAIB",
  "UB",
  "ABRK",
  "ARAB",
  "BBE",
  "ABC",
  "MISR",
  "EGB",
  "POST",
  "MASH",
  "NIB",
  "SCB",
  "BOA",
  "ADIB",
]);

const Base = {
  issuer: z.enum(["VODAFONE", "ETISALAT", "ORANGE", "BANK_WALLET", "BANK_CARD", "INSTANT_BANK", "POST"]),
  nationalId: z
    .string(errorMessage("National ID is required", "Invalid national ID"))
    .regex(/^\d{14}$/, "Invalid national ID"),
};

// Helper function to avoid duplicate logic, allow parametrize bankCode type
function createWithdrawalMethodSchema<T, V, Y>(shape: { bankCard: T; instantBank: V; msisdn: Y }) {
  return z.discriminatedUnion("issuer", [
    z.object({
      ...Base,
      issuer: z.literal("VODAFONE"),
      msisdn: shape.msisdn,
    }),
    z.object({
      ...Base,
      issuer: z.literal("ETISALAT"),
      msisdn: shape.msisdn,
    }),
    z.object({
      ...Base,
      issuer: z.literal("ORANGE"),
      msisdn: shape.msisdn,
    }),
    z.object({
      ...Base,
      issuer: z.literal("BANK_WALLET"),
      msisdn: shape.msisdn,
    }),
    z.object({
      ...Base,
      issuer: z.literal("POST"),
      fullName: z.string(errorMessage("Full name is required", "Full name is invalid")),
    }),
    z.object({
      ...Base,
      issuer: z.literal("BANK_CARD"),
      bankCardNumber: z.string(errorMessage("Bank card number is required", "Bank card number is invalid")),
      bankCode: shape.bankCard,
      fullName: z.string(errorMessage("Full name is required", "Full name is invalid")),
    }),
    z.object({
      ...Base,
      issuer: z.literal("INSTANT_BANK"),
      bankCardNumber: z.string(errorMessage("Bank card number is required", "Bank card number is invalid")),
      bankCode: shape.instantBank,
      fullName: z.string(errorMessage("Full name is required", "Full name is invalid")),
    }),
  ]);
}

// Schema with enum bank codes
const WithdrawalMethodWithBankCodesSchema = createWithdrawalMethodSchema({
  bankCard: BankCardBankCode,
  instantBank: InstantBankBankCode,
  msisdn: Msisdn,
});

// Schema with string bank codes
const WithdrawalMethodSchema = createWithdrawalMethodSchema({
  bankCard: z.string(),
  instantBank: z.string(),
  msisdn: z.string(),
});

export const Withdrawal = {
  WithdrawalMethodWithBankCodes: WithdrawalMethodWithBankCodesSchema,
  WithdrawalMethod: WithdrawalMethodSchema,
  WithdrawalStatus,
};
export type Withdrawal = {
  WithdrawalMethodWithBankCodes: z.infer<typeof WithdrawalMethodWithBankCodesSchema>;
  WithdrawalMethod: z.infer<typeof WithdrawalMethodSchema>;
  WithdrawalStatus: typeof WithdrawalStatus;
};
